import { zodResolver } from '@hookform/resolvers/zod';
import {
  type BetaSignupInput,
  betaSignupSchema,
  type FleetSize,
  fleetSizeOptions,
} from '@sea-snag/shared';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@sea-snag/ui';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useBetaSignup } from '../../hooks/use-beta-signup';

export function LandingBeta() {
  const [submitted, setSubmitted] = useState(false);
  const mutation = useBetaSignup();

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = useForm<BetaSignupInput>({
    resolver: zodResolver(betaSignupSchema),
    defaultValues: { email: '', company: '', fleetSize: '' as FleetSize },
  });

  const onSubmit = handleSubmit(async (data) => {
    await mutation.mutateAsync({ ...data, source: 'homepage' });
    setSubmitted(true);
  });

  return (
    <section id="beta" className="scroll-mt-20 border-t border-border py-32 md:py-40">
      <div className="mx-auto max-w-2xl px-6">
        <p className="mb-4 font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
          Closed beta
        </p>
        <h2 className="mb-6 text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
          We&rsquo;re onboarding charter operators by hand.
        </h2>
        <p className="mb-12 text-base text-muted-foreground md:text-lg">
          Sea Snag is in private beta. We work directly with each operator to set up their
          competitive set, tune alerts, and dial in pricing rules. Spots are limited this season.
        </p>

        {submitted ? (
          <p className="rounded-lg border border-border bg-muted/30 p-6 text-sm text-foreground">
            Thanks. We&rsquo;ll be in touch within 2 business days.
          </p>
        ) : (
          <form onSubmit={onSubmit} className="space-y-5" noValidate>
            <div className="space-y-2">
              <Label htmlFor="beta-email">Email</Label>
              <Input
                id="beta-email"
                type="email"
                autoComplete="email"
                placeholder="captain@yourcharter.com"
                aria-invalid={!!errors.email}
                {...register('email')}
              />
              {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="beta-company">Company</Label>
              <Input
                id="beta-company"
                type="text"
                autoComplete="organization"
                placeholder="Your company"
                aria-invalid={!!errors.company}
                {...register('company')}
              />
              {errors.company && (
                <p className="text-xs text-destructive">{errors.company.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="beta-fleet-size">Fleet size</Label>
              <Controller
                control={control}
                name="fleetSize"
                render={({ field }) => (
                  <Select value={field.value || undefined} onValueChange={field.onChange}>
                    <SelectTrigger id="beta-fleet-size" aria-invalid={!!errors.fleetSize}>
                      <SelectValue placeholder="Select fleet size" />
                    </SelectTrigger>
                    <SelectContent>
                      {fleetSizeOptions.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option} yachts
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.fleetSize && (
                <p className="text-xs text-destructive">{errors.fleetSize.message}</p>
              )}
            </div>

            {mutation.isError && (
              <p className="text-sm text-destructive">
                Something went wrong. Please try again in a moment.
              </p>
            )}

            <div className="pt-2">
              <Button type="submit" size="lg" disabled={isSubmitting || mutation.isPending}>
                {mutation.isPending ? 'Submitting…' : 'Request access →'}
              </Button>
              <p className="mt-3 text-xs text-muted-foreground">
                We&rsquo;ll respond within 2 business days.
              </p>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
